import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useStytch, useStytchSession, useStytchUser } from "@stytch/react";
import { env } from "@/config/env";
import { setAuthTokenGetter } from "@/lib/api/client";

export type AppAuthUser = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  imageUrl: string;
  primaryEmailAddress: { emailAddress: string } | null;
};

type SignUpResult = "complete" | "verify";

export type AppAuth = {
  provider: "stytch" | "development";
  isLoaded: boolean;
  isSignedIn: boolean;
  user: AppAuthUser | null;
  getToken: () => Promise<string | null>;
  signIn: (email: string, password: string) => Promise<void>;
  startSignUp: (email: string, password: string) => Promise<SignUpResult>;
  verifySignUp: (code: string) => Promise<void>;
  resendSignUpCode: () => Promise<void>;
  startPasswordReset: (email: string) => Promise<void>;
  finishPasswordReset: (token: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signOutAll: () => Promise<void>;
};

const AuthContext = createContext<AppAuth | null>(null);

function useStytchAdapter(): AppAuth {
  const stytch = useStytch();
  const { user, isInitialized: userLoaded } = useStytchUser();
  const { session, isInitialized: sessionLoaded } = useStytchSession();
  const pendingSignUp = useRef<{
    methodId: string;
    password: string;
    email: string;
  } | null>(null);

  const getToken = useCallback(async () => {
    const retryDelays = [50, 100, 250, 500, 1_000];

    for (let attempt = 0; attempt <= retryDelays.length; attempt += 1) {
      const tokens = stytch.session.getTokens();
      const token = tokens?.session_jwt || tokens?.session_token || null;
      if (token) return token;

      if (attempt < retryDelays.length) {
        await new Promise((resolve) =>
          window.setTimeout(resolve, retryDelays[attempt]),
        );
      }
    }

    return null;
  }, [stytch]);

  return useMemo(
    () => ({
      provider: "stytch" as const,
      isLoaded: userLoaded && sessionLoaded,
      isSignedIn: Boolean(session && user),
      user: user
        ? {
            id: user.user_id,
            firstName: user.name?.first_name || null,
            lastName: user.name?.last_name || null,
            fullName:
              [user.name?.first_name, user.name?.middle_name, user.name?.last_name]
                .filter(Boolean)
                .join(" ") || null,
            imageUrl: "",
            primaryEmailAddress: user.emails[0]
              ? { emailAddress: user.emails[0].email }
              : null,
          }
        : null,
      getToken,
      signIn: async (email: string, password: string) => {
        await stytch.passwords.authenticate({
          email: email.trim(),
          password,
          session_duration_minutes: env.stytchSessionMinutes,
        });
      },
      startSignUp: async (email: string, password: string) => {
        const result = await stytch.otps.email.loginOrCreate(email.trim(), {
          expiration_minutes: 10,
        });
        pendingSignUp.current = {
          methodId: result.method_id,
          password,
          email: email.trim(),
        };
        return "verify" as const;
      },
      verifySignUp: async (code: string) => {
        const pending = pendingSignUp.current;
        if (!pending) throw new Error("Start sign up again");
        await stytch.otps.authenticate(code, pending.methodId, {
          session_duration_minutes: 5,
        });
        await stytch.passwords.resetBySession({
          password: pending.password,
          session_duration_minutes: env.stytchSessionMinutes,
        });
        pendingSignUp.current = null;
      },
      resendSignUpCode: async () => {
        const pending = pendingSignUp.current;
        if (!pending) throw new Error("Start sign up again");
        const result = await stytch.otps.email.send(pending.email, {
          expiration_minutes: 10,
        });
        pending.methodId = result.method_id;
      },
      startPasswordReset: async (email: string) => {
        await stytch.passwords.resetByEmailStart({
          email: email.trim(),
          reset_password_redirect_url: `${window.location.origin}/reset-password`,
          login_redirect_url: `${window.location.origin}/login`,
        });
      },
      finishPasswordReset: async (token: string, password: string) => {
        await stytch.passwords.resetByEmail({
          token,
          password,
          session_duration_minutes: env.stytchSessionMinutes,
        });
      },
      signOut: async () => {
        if (stytch.session.getSync()) await stytch.session.revoke();
      },
      signOutAll: async () => {
        const token = await getToken();
        if (token) {
          await fetch(`${env.apiUrl}/api/auth/sessions/revoke-all`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          });
        }
        stytch.session.updateSession({ session_token: "", session_jwt: null });
      },
    }),
    [stytch, user, userLoaded, session, sessionLoaded, getToken],
  );
}

function BindAuth({ value, children }: { value: AppAuth; children: ReactNode }) {
  useEffect(() => setAuthTokenGetter(value.getToken), [value.getToken]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function StytchAuthBridge({ children }: { children: ReactNode }) {
  return <BindAuth value={useStytchAdapter()}>{children}</BindAuth>;
}

export function DevelopmentAuthBridge({ children }: { children: ReactNode }) {
  const value = useMemo<AppAuth>(
    () => ({
      provider: "development",
      isLoaded: true,
      isSignedIn: true,
      user: {
        id: "dev_user",
        firstName: "Dev",
        lastName: "User",
        fullName: "Dev User",
        imageUrl: "",
        primaryEmailAddress: { emailAddress: "dev@local.host" },
      },
      getToken: async () => null,
      signIn: async () => undefined,
      startSignUp: async () => "complete",
      verifySignUp: async () => undefined,
      resendSignUpCode: async () => undefined,
      startPasswordReset: async () => undefined,
      finishPasswordReset: async () => undefined,
      signOut: async () => undefined,
      signOutAll: async () => undefined,
    }),
    [],
  );
  return <BindAuth value={value}>{children}</BindAuth>;
}

export function useAppAuth(): AppAuth {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAppAuth must be used inside an auth provider");
  return value;
}
